import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';
import PhoneInput from '../../../../components/form/PhoneInput';
import { COUNTRY_OPTIONS } from '../../../../components/form/PhoneInput';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type SupplierRow = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  country?: string | null;
  city?: string | null;
  isActive: boolean;
};

type PaginatedSuppliers = {
  items: SupplierRow[];
  total: number;
  page: number;
  pageSize: number;
};

function getCountryName(code: string): string {
  try {
    if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
      // Use browser locale when available; fallback to English.
      const locale = navigator.language || 'en';
      const displayNames = new (Intl as any).DisplayNames([locale, 'en'], {
        type: 'region',
      });
      const name = displayNames.of(code);
      if (typeof name === 'string') return name;
    }
  } catch {
    // ignore and fallback
  }
  return code;
}

function SuppliersPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierRow | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formCountry, setFormCountry] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  const pageSize = 20;

  const loadSuppliers = React.useCallback(async (pageToLoad: number) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/suppliers`);
      url.searchParams.set('page', String(pageToLoad));
      url.searchParams.set('pageSize', String(pageSize));
      if (search) url.searchParams.set('q', search);
      if (activeOnly) url.searchParams.set('activeOnly', 'true');
      const res = await authorizedFetch(url.toString());
      if (!res.ok) throw new Error(t('backoffice.suppliers_load_error', 'Unable to load suppliers.'));
      const data = (await res.json()) as PaginatedSuppliers;
      setSuppliers(data.items);
      setTotal(data.total);
      setPage(data.page);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.suppliers_load_error', 'Unable to load suppliers.'),
      );
    } finally {
      setLoading(false);
    }
  }, [activeOnly, authorizedFetch, pageSize, search, t]);

  useEffect(() => {
    void loadSuppliers(1);
  }, [loadSuppliers]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormPhone('');
    setFormCountry('');
    setFormCity('');
    setFormAddress('');
    setFormNotes('');
    setEditingSupplier(null);
  };

  const startCreate = () => {
    setError(null);
    resetForm();
    setEditingSupplier(null);
    setShowForm(true);
  };

  const startEdit = (supplier: SupplierRow) => {
    setError(null);
    setEditingSupplier(supplier);
    setFormName(supplier.name);
    setFormEmail(supplier.email ?? '');
    setFormPhone(supplier.phone ?? '');
    setFormCountry(supplier.country ?? '');
    setFormCity(supplier.city ?? '');
    setFormAddress(supplier.address ?? '');
    setFormNotes(supplier.notes ?? '');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSaving(true);
    setError(null);
    try {
      const payload = {
        name: formName,
        email: formEmail || undefined,
        phone: formPhone || undefined,
        country: formCountry || undefined,
        city: formCity || undefined,
        address: formAddress || undefined,
        notes: formNotes || undefined,
      };

      const url = editingSupplier
        ? `${API_BASE}/suppliers/${editingSupplier.id}`
        : `${API_BASE}/suppliers`;

      const method = editingSupplier ? 'PATCH' : 'POST';

      const res = await authorizedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message
            || t('backoffice.suppliers_save_error', 'Unable to save supplier.'),
        );
      }
      resetForm();
      setShowForm(false);
      void loadSuppliers(1);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.suppliers_save_error', 'Unable to save supplier.'),
      );
    } finally {
      setFormSaving(false);
    }
  };

  const toggleActive = async (supplier: SupplierRow) => {
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/suppliers/${supplier.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !supplier.isActive }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message
            || t('backoffice.suppliers_update_error', 'Unable to update supplier.'),
        );
      }
      await loadSuppliers(page);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.suppliers_update_error', 'Unable to update supplier.'),
      );
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.suppliers_title', 'Suppliers')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t(
              'admin.suppliers_subtitle',
              'Manage your suppliers. Only employees can create and edit suppliers; suppliers do not log in.',
            )}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('backoffice.suppliers_search', 'Search by name, email or phone')}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:w-64"
              />
              <label className="inline-flex items-center gap-1 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                />
                <span>{t('backoffice.suppliers_active_only', 'Active only')}</span>
              </label>
            </div>
            <button
              type="button"
              onClick={() => (showForm ? setShowForm(false) : startCreate())}
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500"
            >
              {showForm
                ? t('backoffice.suppliers_hide_form', 'Hide form')
                : t('backoffice.suppliers_new', 'New supplier')}
            </button>
          </div>

          {error && (
            <p className="mb-3 text-xs text-red-500">
              {error}
            </p>
          )}

          {showForm && (
            <form
              onSubmit={handleSubmit}
              className="mb-6 grid gap-3 border-b border-slate-100 pb-5 text-xs sm:grid-cols-2"
            >
              <div className="space-y-1">
                <label className="block font-medium text-slate-700" htmlFor="supplierName">
                  {t('backoffice.suppliers_name', 'Name')}
                </label>
                <input
                  id="supplierName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="block font-medium text-slate-700" htmlFor="supplierEmail">
                  {t('backoffice.suppliers_email', 'Email')}
                </label>
                <input
                  id="supplierEmail"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>

              <div className="space-y-1">
                <PhoneInput
                  id="supplierPhone"
                  label={t('backoffice.suppliers_phone', 'Phone')}
                  value={formPhone}
                  onChange={setFormPhone}
                />
              </div>

              <div className="space-y-1">
                <label className="block font-medium text-slate-700" htmlFor="supplierCountry">
                  {t('backoffice.suppliers_country', 'Country')}
                </label>
                <select
                  id="supplierCountry"
                  value={formCountry}
                  onChange={(e) => setFormCountry(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                >
                  <option value="">
                    {t('backoffice.suppliers_country_placeholder', 'Select a country')}
                  </option>
                  {COUNTRY_OPTIONS.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {getCountryName(country.code)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-medium text-slate-700" htmlFor="supplierCity">
                  {t('backoffice.suppliers_city', 'Town / City')}
                </label>
                <input
                  id="supplierCity"
                  value={formCity}
                  onChange={(e) => setFormCity(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="block font-medium text-slate-700" htmlFor="supplierAddress">
                  {t('backoffice.suppliers_address', 'Address')}
                </label>
                <input
                  id="supplierAddress"
                  value={formAddress}
                  onChange={(e) => setFormAddress(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="block font-medium text-slate-700" htmlFor="supplierNotes">
                  {t('backoffice.suppliers_notes', 'Notes')}
                </label>
                <input
                  id="supplierNotes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>

              <div className="flex items-center gap-3 sm:col-span-2">
                <button
                  type="submit"
                  disabled={formSaving}
                  className="rounded-md bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-60"
                >
                  {formSaving
                    ? t('backoffice.products_saving', 'Saving…')
                    : editingSupplier
                      ? t('backoffice.suppliers_update', 'Update supplier')
                      : t('backoffice.suppliers_save', 'Save supplier')}
                </button>
                {showForm && (
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    className="text-xs font-medium text-slate-500 hover:text-slate-700"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                )}
              </div>
            </form>
          )}

          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.suppliers_loading', 'Loading suppliers…')}
              skeletonRows={5}
            />
          ) : suppliers.length === 0 ? (
            <p className="text-sm text-slate-600">
              {t('backoffice.suppliers_empty', 'No suppliers found.')}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_name', 'Name')}
                      </th>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_email', 'Email')}
                      </th>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_phone', 'Phone')}
                      </th>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_country', 'Country')}
                      </th>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_city', 'Town / City')}
                      </th>
                      <th className="py-2 pr-4">
                        {t('backoffice.suppliers_col_status', 'Status')}
                      </th>
                      <th className="py-2 pr-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {suppliers.map((s) => (
                      <tr key={s.id}>
                        <td className="py-2 pr-4 text-slate-900">
                          {s.name}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {s.email || t('common.empty_placeholder', '—')}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {s.phone || t('common.empty_placeholder', '—')}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {s.country ? getCountryName(s.country) : t('common.empty_placeholder', '—')}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {s.city || t('common.empty_placeholder', '—')}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${
                              s.isActive
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {s.isActive
                              ? t('backoffice.suppliers_status_active', 'Active')
                              : t('backoffice.suppliers_status_inactive', 'Inactive')}
                          </span>
                        </td>
                        <td className="py-2 pr-4 space-x-2">
                          <button
                            type="button"
                            onClick={() => startEdit(s)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                          >
                            {t('backoffice.suppliers_edit', 'Edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => void toggleActive(s)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                          >
                            {s.isActive
                              ? t('backoffice.suppliers_deactivate', 'Deactivate')
                              : t('backoffice.suppliers_activate', 'Activate')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-600">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => void loadSuppliers(page - 1)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.inventory_prev_page', 'Prev')}
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => void loadSuppliers(page + 1)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.inventory_next_page', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default SuppliersPage;

